import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardApi } from "@/services/api";
import { AppHeader } from "@/components/layout/AppHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, 
  DollarSign, 
  FileText, 
  ClipboardList, 
  Car, 
  TrendingUp,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  Wrench
} from "lucide-react";

const statusColors = {
  paid: "bg-success/20 text-success",
  pending: "bg-warning/20 text-warning",
  overdue: "bg-destructive/20 text-destructive",
  active: "bg-primary/20 text-primary",
  completed: "bg-success/20 text-success",
  available: "bg-success/20 text-success",
  rented: "bg-primary/20 text-primary",
  maintenance: "bg-warning/20 text-warning"
};

const categoryColors = {
  sedan: "#3b82f6",
  suv: "#10b981",
  truck: "#f59e0b",
  luxury: "#8b5cf6",
  electric: "#06b6d4",
  van: "#ec4899"
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await dashboardApi.getStats();
        setStats(data);
      } catch (error) {
        console.error("Failed to load dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </main>
      </div>
    );
  }

  const maxRevenue = Math.max(...(stats?.revenue?.monthly?.map(m => m.revenue) || [1]));

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10">
            <BarChart3 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-sm text-muted-foreground">Business overview and analytics</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Monthly Revenue */}
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Monthly Revenue</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-1">
                    {formatCurrency(stats?.revenue?.currentMonth)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(stats?.revenue?.pending)} pending
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Invoices</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{stats?.invoices?.total}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs text-success flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> {stats?.invoices?.paid} paid
                    </span>
                    <span className="text-xs text-warning flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {stats?.invoices?.pending} pending
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Agreements */}
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/agreements')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Agreements</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">{stats?.agreements?.total}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs text-primary flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> {stats?.agreements?.active} active
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fleet */}
          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/vehicles')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Fleet Vehicles</p>
                  <p className="text-3xl font-bold text-amber-600 mt-1">{stats?.fleet?.total}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs text-success flex items-center gap-1">
                      <Car className="w-3 h-3" /> {stats?.fleet?.available} available
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Car className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Monthly Revenue
              </CardTitle>
              <CardDescription>Revenue trend over the last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-end justify-between gap-2 pt-4">
                {stats?.revenue?.monthly?.map((month, i) => (
                  <div key={i} className="flex flex-col items-center flex-1">
                    <div 
                      className="w-full bg-primary/80 rounded-t-md transition-all hover:bg-primary"
                      style={{ 
                        height: `${(month.revenue / maxRevenue) * 180}px`,
                        minHeight: '20px'
                      }}
                      title={formatCurrency(month.revenue)}
                    />
                    <span className="text-xs text-muted-foreground mt-2">{month.month}</span>
                    <span className="text-xs font-medium">{formatCurrency(month.revenue)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Fleet Utilization */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Car className="w-5 h-5 text-primary" />
                Fleet Utilization
              </CardTitle>
              <CardDescription>Current vehicle status breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Utilization Rate */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-muted-foreground">Utilization Rate</span>
                  <span className="text-2xl font-bold text-primary">{stats?.fleet?.utilizationRate}%</span>
                </div>
                <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${stats?.fleet?.utilizationRate || 0}%` }}
                  />
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-success/10">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-success" />
                    <span className="text-sm">Available</span>
                  </div>
                  <span className="font-bold text-success">{stats?.fleet?.available}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-primary" />
                    <span className="text-sm">Rented</span>
                  </div>
                  <span className="font-bold text-primary">{stats?.fleet?.rented}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-warning/10">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-warning" />
                    <span className="text-sm">Maintenance</span>
                  </div>
                  <span className="font-bold text-warning">{stats?.fleet?.maintenance}</span>
                </div>
              </div>

              {/* Category Distribution */}
              {stats?.fleet?.categories && Object.keys(stats.fleet.categories).length > 0 && (
                <div className="mt-6">
                  <p className="text-sm text-muted-foreground mb-3">Vehicle Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(stats.fleet.categories).map(([cat, count]) => (
                      <Badge 
                        key={cat} 
                        variant="secondary"
                        style={{ backgroundColor: `${categoryColors[cat]}20`, color: categoryColors[cat] }}
                      >
                        {cat}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Invoices */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Recent Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.recentActivity?.invoices?.length > 0 ? (
                  stats.recentActivity.invoices.map((invoice) => (
                    <div 
                      key={invoice.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/invoice/${invoice.id}`)}
                    >
                      <div>
                        <p className="font-medium text-sm">{invoice.customer || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{invoice.id}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{invoice.date}</span>
                        <Badge variant="secondary" className={statusColors[invoice.status]}>
                          {invoice.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No recent invoices</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Agreements */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                Recent Agreements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.recentActivity?.agreements?.length > 0 ? (
                  stats.recentActivity.agreements.map((agreement) => (
                    <div 
                      key={agreement.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/agreement/${agreement.id}`)}
                    >
                      <div>
                        <p className="font-medium text-sm">{agreement.renter || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{agreement.vehicle || agreement.id}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{agreement.date}</span>
                        <Badge variant="secondary" className={statusColors[agreement.status]}>
                          {agreement.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No recent agreements</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
